import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  UploadCloud,
  ArrowLeft,
  ArrowRight,
  FileCheck2,
  AlertCircle,
} from "lucide-react";

/* -------- CSV parser (RFC-4180 compatible with configurable delimiter) -------- */
function stripBOM(text) {
  if (text && text.charCodeAt(0) === 0xfeff) return text.slice(1);
  return text;
}

function detectDelimiter(text) {
  const firstLine = (text.split("\n")[0] || "").trim();
  const counts = { ";": 0, ",": 0, "\t": 0, "|": 0 };
  let inQuotes = false;
  for (const ch of firstLine) {
    if (ch === '"') inQuotes = !inQuotes;
    else if (!inQuotes && counts[ch] !== undefined) counts[ch]++;
  }
  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  return sorted[0][1] > 0 ? sorted[0][0] : ",";
}

function parseCSV(text, delimiter) {
  const rows = [];
  let cur = "";
  let inQuotes = false;
  let row = [];
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') {
        cur += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        cur += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === delimiter) {
        row.push(cur);
        cur = "";
      } else if (ch === "\n") {
        row.push(cur);
        rows.push(row);
        row = [];
        cur = "";
      } else if (ch === "\r") {
        // ignore
      } else {
        cur += ch;
      }
    }
  }
  if (cur !== "" || row.length > 0) {
    row.push(cur);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c && c.trim().length > 0));
}

/* -------- Email extraction -------- */
const EMAIL_RE = /[a-zA-Z0-9._%+\-']+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
function extractEmails(v) {
  if (!v) return [];
  const matches = String(v).match(EMAIL_RE) || [];
  return matches.map((e) => e.trim().toLowerCase());
}

const DELIMITER_LABELS = {
  ";": "Punto e virgola  ( ; )",
  ",": "Virgola  ( , )",
  "\t": "Tabulazione  ( TAB )",
  "|": "Barra  ( | )",
};

export default function ImportCsvPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [rawText, setRawText] = useState("");
  const [delimiter, setDelimiter] = useState(",");
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [canonical, setCanonical] = useState([]);
  const [lists, setLists] = useState([]);
  const [selectedList, setSelectedList] = useState("__none__");
  const [dedupe, setDedupe] = useState(true);
  const [multiEmailMode, setMultiEmailMode] = useState("first"); // "first" | "explode"
  const [progress, setProgress] = useState({ done: 0, total: 0, running: false });
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/contacts/canonical-fields").then(({ data }) => setCanonical(data.fields));
    api.get("/lists").then(({ data }) => setLists(data));
  }, []);

  /* ----- Reparse when delimiter or raw text changes ----- */
  useEffect(() => {
    if (!rawText) return;
    const parsed = parseCSV(rawText, delimiter);
    if (parsed.length < 2) {
      setHeaders([]);
      setRows([]);
      return;
    }
    const hdrs = parsed[0].map((h) => (h || "").trim());
    const data = parsed.slice(1).map((r) => {
      const obj = {};
      hdrs.forEach((h, i) => (obj[h] = (r[i] ?? "").trim()));
      return obj;
    });
    setHeaders(hdrs);
    setRows(data);

    // Auto-mapping
    const initial = {};
    hdrs.forEach((h) => {
      const low = h.toLowerCase();
      if (low.includes("mail") || low === "e-mail") initial[h] = "email";
      else if (low === "nome" || low.startsWith("nome ") || low.includes("first"))
        initial[h] = "first_name";
      else if (low === "cognome" || low.includes("last") || low.includes("surname"))
        initial[h] = "last_name";
      else if (
        low.includes("ragione") ||
        low.includes("azienda") ||
        low.includes("company") ||
        low.includes("business")
      )
        initial[h] = "company";
      else initial[h] = "__ignore__";
    });
    setMapping(initial);
  }, [rawText, delimiter]);

  const onFile = async (f) => {
    if (!f) return;
    setFile(f);
    const text = stripBOM(await f.text());
    setRawText(text);
    const detected = detectDelimiter(text);
    setDelimiter(detected);
    setStep(2);
  };

  /* ---------- Stats for preview ---------- */
  const stats = useMemo(() => {
    const emailCol = Object.entries(mapping).find(([, v]) => v === "email")?.[0];
    if (!emailCol) return { validRows: 0, totalRows: rows.length, skippedNoEmail: 0, multiEmailRows: 0, totalEmails: 0 };
    let valid = 0;
    let noEmail = 0;
    let multi = 0;
    let totalEmails = 0;
    for (const r of rows) {
      const emails = extractEmails(r[emailCol]);
      if (emails.length === 0) noEmail++;
      else {
        valid++;
        totalEmails += multiEmailMode === "explode" ? emails.length : 1;
        if (emails.length > 1) multi++;
      }
    }
    return {
      validRows: valid,
      totalRows: rows.length,
      skippedNoEmail: noEmail,
      multiEmailRows: multi,
      totalEmails,
    };
  }, [rows, mapping, multiEmailMode]);

  const preview = useMemo(() => rows.slice(0, 5), [rows]);

  /* ---------- Build payload rows honoring multi-email mode ---------- */
  const buildImportRows = () => {
    const emailCol = Object.entries(mapping).find(([, v]) => v === "email")?.[0];
    const out = [];
    for (const r of rows) {
      const emails = extractEmails(r[emailCol]);
      if (emails.length === 0) continue;
      const selectedEmails = multiEmailMode === "explode" ? emails : [emails[0]];
      for (const e of selectedEmails) {
        // clone the row but override the mapped email column with the single value
        out.push({ ...r, [emailCol]: e });
      }
    }
    return out;
  };

  const runImport = async () => {
    const emailMapped = Object.values(mapping).includes("email");
    if (!emailMapped) return toast.error("Devi mappare una colonna alla email");

    const allRows = buildImportRows();
    if (allRows.length === 0)
      return toast.error("Nessuna riga con email valida nel file");

    const CHUNK = 500;
    setProgress({ done: 0, total: allRows.length, running: true });
    setStep(3);
    const agg = { inserted: 0, updated: 0, skipped: 0, total_processed: 0 };

    try {
      for (let i = 0; i < allRows.length; i += CHUNK) {
        const chunk = allRows.slice(i, i + CHUNK);
        const { data } = await api.post("/contacts/import", {
          rows: chunk,
          mapping,
          list_ids:
            selectedList && selectedList !== "__none__" ? [selectedList] : [],
          dedupe,
        });
        agg.inserted += data.inserted || 0;
        agg.updated += data.updated || 0;
        agg.skipped += data.skipped || 0;
        agg.total_processed += data.total_processed || 0;
        setProgress({ done: Math.min(i + CHUNK, allRows.length), total: allRows.length, running: true });
      }
      setResult(agg);
      setProgress((p) => ({ ...p, running: false }));
      toast.success(
        `Importati ${agg.inserted} nuovi, ${agg.updated} aggiornati`
      );
    } catch (err) {
      toast.error(err?.friendlyMessage || "Errore durante l'importazione");
      setProgress((p) => ({ ...p, running: false }));
    }
  };

  const restart = () => {
    setStep(1);
    setFile(null);
    setRawText("");
    setRows([]);
    setHeaders([]);
    setResult(null);
    setProgress({ done: 0, total: 0, running: false });
  };

  const emailColumn = Object.entries(mapping).find(([, v]) => v === "email")?.[0];

  return (
    <>
      <PageHeader
        title="Importa contatti da CSV"
        subtitle="Wizard guidato: rileva il formato del file, mappa le colonne, gestisce email multiple e importa a blocchi."
        testid="import-header"
        actions={
          <Button
            variant="outline"
            onClick={() => navigate("/contatti")}
            data-testid="back-to-contacts"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Contatti
          </Button>
        }
      />
      <PageBody>
        {/* Stepper */}
        <div className="flex items-center gap-2 mb-8 text-xs uppercase tracking-widest text-muted-foreground">
          <span className={step >= 1 ? "text-foreground" : ""}>1. Carica</span>
          <span>·</span>
          <span className={step >= 2 ? "text-foreground" : ""}>2. Mappa &amp; anteprima</span>
          <span>·</span>
          <span className={step >= 3 ? "text-foreground" : ""}>3. Import</span>
          <span>·</span>
          <span className={step >= 4 ? "text-foreground" : ""}>4. Risultato</span>
        </div>

        {/* ---------------- STEP 1: Upload ---------------- */}
        {step === 1 && (
          <div className="border border-dashed border-border bg-white p-16 text-center">
            <UploadCloud
              className="w-8 h-8 mx-auto text-muted-foreground mb-4"
              strokeWidth={1.25}
            />
            <h2 className="font-heading text-xl mb-2">Carica il tuo CSV</h2>
            <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
              Supporta delimitatori virgola, punto e virgola o tabulazione. Rileva
              automaticamente il formato e ignora BOM UTF-8. Le celle con più
              email verranno gestite nel prossimo step.
            </p>
            <input
              id="csv-input"
              type="file"
              accept=".csv,text/csv,text/plain"
              className="hidden"
              onChange={(e) => onFile(e.target.files?.[0])}
              data-testid="csv-file-input"
            />
            <Button
              onClick={() => document.getElementById("csv-input").click()}
              data-testid="csv-select-btn"
            >
              Seleziona file CSV
            </Button>
          </div>
        )}

        {/* ---------------- STEP 2: Map & preview ---------------- */}
        {step === 2 && (
          <div className="space-y-6">
            {/* File info + delimiter override */}
            <div className="border border-border bg-white p-4 grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
              <div className="flex items-center gap-3 md:col-span-2">
                <FileCheck2 className="w-5 h-5 text-primary shrink-0" strokeWidth={1.5} />
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{file?.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {rows.length} righe · {headers.length} colonne · delimitatore rilevato:{" "}
                    <code className="bg-muted px-1">
                      {delimiter === "\t" ? "TAB" : delimiter}
                    </code>
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-xs">Cambia delimitatore</Label>
                <Select value={delimiter} onValueChange={setDelimiter}>
                  <SelectTrigger data-testid="delimiter-select" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(DELIMITER_LABELS).map(([d, l]) => (
                      <SelectItem key={d} value={d}>
                        {l}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Mapping */}
            <div className="border border-border bg-white">
              <div className="border-b border-border px-6 py-4">
                <h2 className="font-heading text-lg">Mappa le colonne</h2>
                <p className="text-xs text-muted-foreground mt-1">
                  Almeno una colonna deve essere mappata a "Email". I campi
                  marcati "Ignora" non verranno importati.
                </p>
              </div>
              <div className="p-6 space-y-3 max-h-96 overflow-y-auto">
                {headers.map((h) => (
                  <div
                    key={h}
                    className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center border-b border-border pb-3 last:border-0"
                    data-testid={`map-row-${h}`}
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{h}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        es. {preview[0]?.[h] || <span className="italic">vuoto</span>}
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <Select
                        value={mapping[h] || "__ignore__"}
                        onValueChange={(v) => setMapping({ ...mapping, [h]: v })}
                      >
                        <SelectTrigger data-testid={`map-select-${h}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {canonical.map((f) => (
                            <SelectItem key={f.key} value={f.key}>
                              {f.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Preview */}
            {emailColumn && (
              <div className="border border-border bg-white">
                <div className="border-b border-border px-6 py-4 flex items-center justify-between">
                  <div>
                    <h2 className="font-heading text-lg">Anteprima primi 5 contatti</h2>
                    <p className="text-xs text-muted-foreground mt-1">
                      Ecco come verranno importati con la mappatura attuale.
                    </p>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left px-4 py-2 text-xs uppercase tracking-widest text-muted-foreground">Email</th>
                        <th className="text-left px-4 py-2 text-xs uppercase tracking-widest text-muted-foreground">Nome</th>
                        <th className="text-left px-4 py-2 text-xs uppercase tracking-widest text-muted-foreground">Cognome</th>
                        <th className="text-left px-4 py-2 text-xs uppercase tracking-widest text-muted-foreground">Azienda</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.map((r, idx) => {
                        const emails = extractEmails(r[emailColumn]);
                        const email =
                          multiEmailMode === "explode"
                            ? emails.join(", ")
                            : emails[0] || "—";
                        const firstNameCol = Object.entries(mapping).find(([, v]) => v === "first_name")?.[0];
                        const lastNameCol = Object.entries(mapping).find(([, v]) => v === "last_name")?.[0];
                        const companyCol = Object.entries(mapping).find(([, v]) => v === "company")?.[0];
                        return (
                          <tr key={idx} className="border-b border-border last:border-0">
                            <td className="px-4 py-2 font-medium">
                              {email === "—" ? (
                                <span className="text-muted-foreground">— (verrà saltata)</span>
                              ) : (
                                <>
                                  {email}
                                  {emails.length > 1 && (
                                    <Badge variant="secondary" className="ml-2">
                                      +{emails.length - 1}
                                    </Badge>
                                  )}
                                </>
                              )}
                            </td>
                            <td className="px-4 py-2 text-muted-foreground">
                              {(firstNameCol && r[firstNameCol]) || "—"}
                            </td>
                            <td className="px-4 py-2 text-muted-foreground">
                              {(lastNameCol && r[lastNameCol]) || "—"}
                            </td>
                            <td className="px-4 py-2 text-muted-foreground">
                              {(companyCol && r[companyCol]) || "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Stats box */}
            {emailColumn && (
              <div className="border border-border bg-white p-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <div className="font-heading text-2xl">{stats.totalRows}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Righe totali</div>
                </div>
                <div>
                  <div className="font-heading text-2xl text-primary">{stats.totalEmails}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Contatti da importare</div>
                </div>
                <div>
                  <div className="font-heading text-2xl text-muted-foreground">{stats.skippedNoEmail}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Righe senza email</div>
                </div>
                <div>
                  <div className="font-heading text-2xl">{stats.multiEmailRows}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Righe con più email</div>
                </div>
              </div>
            )}

            {/* Options */}
            <div className="border border-border bg-white p-6 space-y-4">
              <h3 className="font-heading text-lg">Opzioni di importazione</h3>

              <div>
                <Label>Come gestire le celle con più email?</Label>
                <Select value={multiEmailMode} onValueChange={setMultiEmailMode}>
                  <SelectTrigger className="mt-2" data-testid="multi-email-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="first">Prendi solo la prima email</SelectItem>
                    <SelectItem value="explode">
                      Crea un contatto separato per ogni email
                    </SelectItem>
                  </SelectContent>
                </Select>
                {stats.multiEmailRows > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {stats.multiEmailRows} righe contengono più email nella colonna scelta.
                  </p>
                )}
              </div>

              <div>
                <Label>Aggiungi tutti a una lista (opzionale)</Label>
                <Select value={selectedList} onValueChange={setSelectedList}>
                  <SelectTrigger className="mt-2" data-testid="import-list-select">
                    <SelectValue placeholder="Nessuna" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Nessuna</SelectItem>
                    {lists.map((l) => (
                      <SelectItem key={l.id} value={l.id}>
                        {l.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={dedupe}
                  onCheckedChange={setDedupe}
                  data-testid="import-dedupe"
                />
                Deduplica per email (i duplicati vengono aggiornati anziché duplicati)
              </label>
            </div>

            {!emailColumn && (
              <div className="border border-destructive/30 bg-destructive/5 p-4 flex items-start gap-3">
                <AlertCircle
                  className="w-5 h-5 text-destructive shrink-0"
                  strokeWidth={1.5}
                />
                <div className="text-sm">
                  <div className="font-medium text-destructive">
                    Nessuna colonna mappata a "Email"
                  </div>
                  <div className="text-muted-foreground mt-1">
                    Devi mappare almeno una colonna alla email prima di procedere.
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={restart}>
                Cambia file
              </Button>
              <Button
                onClick={runImport}
                disabled={!emailColumn || stats.totalEmails === 0}
                data-testid="import-run-btn"
              >
                Importa {stats.totalEmails > 0 ? `${stats.totalEmails} contatti` : ""}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* ---------------- STEP 3: Progress ---------------- */}
        {step === 3 && progress.running && (
          <div className="border border-border bg-white p-10 max-w-2xl mx-auto text-center space-y-6">
            <UploadCloud
              className="w-8 h-8 mx-auto text-primary animate-pulse"
              strokeWidth={1.25}
            />
            <h2 className="font-heading text-2xl">Importazione in corso…</h2>
            <p className="text-sm text-muted-foreground">
              {progress.done} di {progress.total} contatti processati
            </p>
            <Progress
              value={progress.total ? (progress.done / progress.total) * 100 : 0}
              className="h-2"
              data-testid="import-progress"
            />
            <p className="text-xs text-muted-foreground">
              Non chiudere la pagina. L'invio avviene a blocchi di 500 righe.
            </p>
          </div>
        )}

        {/* ---------------- STEP 4: Result ---------------- */}
        {step === 3 && !progress.running && result && (
          <div className="border border-border bg-white p-10 text-center max-w-2xl mx-auto">
            <FileCheck2
              className="w-10 h-10 mx-auto text-primary mb-4"
              strokeWidth={1.5}
            />
            <h2 className="font-heading text-2xl mb-2">Import completato</h2>
            <div className="grid grid-cols-3 gap-4 my-8">
              <div>
                <div className="font-heading text-3xl text-primary">
                  {result.inserted}
                </div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">
                  Nuovi
                </div>
              </div>
              <div>
                <div className="font-heading text-3xl">{result.updated}</div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">
                  Aggiornati
                </div>
              </div>
              <div>
                <div className="font-heading text-3xl text-muted-foreground">
                  {result.skipped}
                </div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">
                  Saltati
                </div>
              </div>
            </div>
            <div className="flex gap-2 justify-center">
              <Button
                variant="outline"
                onClick={restart}
                data-testid="import-again"
              >
                Importa un altro
              </Button>
              <Button
                onClick={() => navigate("/contatti")}
                data-testid="import-done"
              >
                Vai ai contatti
              </Button>
            </div>
          </div>
        )}
      </PageBody>
    </>
  );
}
